import { ErrorBoundaryScreen, type ErrorBoundaryAction } from '@verifime/components';
import { getSignedInUserId } from '@verifime/cognito-passwordless-auth';
import {
  buildNewRelicAttributes,
  computeErrorSignature,
  generateErrorReference,
  getOrCreateSessionId,
  recordOccurrence,
  reportErrorToNewRelic,
  routeWithoutQuery,
} from '@verifime/utils';
import { Component, ErrorInfo, ReactNode } from 'react';
import { getOrgIdFromUrl } from '@/utils';
import * as messages from '@/utils/messages';

type Props = { children: ReactNode };
type State = { hasError: boolean; reference: string; notified: boolean };

/**
 * Intentionally a class component, even as the codebase moves to function
 * components. React error boundaries can only be classes: there is no
 * functional or hook equivalent for the error-catching lifecycle
 * (`static getDerivedStateFromError` and `componentDidCatch`). The only
 * alternative is the `react-error-boundary` package, which still wraps a class
 * internally. See https://react.dev/reference/react/Component (Catching
 * rendering errors with an Error Boundary).
 */
export default class PortalErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, reference: '', notified: false };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const componentStack = info.componentStack ?? '';
    const signature = computeErrorSignature(error, componentStack);
    const storage = typeof window !== 'undefined' ? window.sessionStorage : null;
    const { count } = recordOccurrence(signature, Date.now(), storage);
    const reference = generateErrorReference();
    const sessionId = getOrCreateSessionId(storage);
    const route = typeof window !== 'undefined' ? routeWithoutQuery(window.location.pathname) : '';

    let userId: string | undefined;
    try {
      userId = getSignedInUserId() ?? undefined;
    } catch {
      // best-effort identity locator; omit when unavailable
    }

    let orgId: string | undefined;
    try {
      orgId = getOrgIdFromUrl() ?? undefined;
    } catch {
      // best-effort; omit when unavailable
    }

    const notified = reportErrorToNewRelic(
      error,
      buildNewRelicAttributes(componentStack, {
        product: 'management',
        errorReference: reference,
        errorSignature: signature,
        occurrenceCount: count,
        route,
        appVersion: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? 'local',
        environment: process.env.NEXT_PUBLIC_VERCEL_ENV ?? 'local',
        sessionId,
        userId,
        orgId,
      }),
    );
    this.setState({ reference, notified });
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    const { reference, notified } = this.state;

    const primaryAction: ErrorBoundaryAction = {
      label: messages.ERROR_ACTION_BACK_TO_DASHBOARD,
      onClick: () => window.location.assign('/dashboard'),
      variant: 'contained',
      'data-action': 'dashboard',
    };
    const secondaryAction: ErrorBoundaryAction = {
      label: messages.ERROR_ACTION_TRY_AGAIN,
      onClick: () => window.location.reload(),
      variant: 'outlined',
      'data-action': 'retry',
    };

    return (
      <ErrorBoundaryScreen
        data-testid="portal-error-boundary"
        product="management"
        headline={messages.ERROR_HEADLINE}
        body={messages.ERROR_BODY}
        securityNote={messages.ERROR_SECURITY_NOTE}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        referenceCode={reference}
        showNotified={notified}
        notifiedText={messages.ERROR_NOTIFIED}
      />
    );
  }
}
